import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Scatter, ScatterChart, ZAxis } from 'recharts';
import { formatInputValue, getNumericInputProps } from './utils/inputHelpers.js';

const SMSFPerformanceOptimization = ({ smsfState, setSmsfState }) => {
    const [activeTab, setActiveTab] = useState('overview');
    const [performanceData, setPerformanceData] = useState({});
    const [optimizationOpportunities, setOptimizationOpportunities] = useState([]);
    const [benchmarkAnalysis, setBenchmarkAnalysis] = useState({});
    
    // Portfolio Optimizer (Tests 848-854)
    const [optimizationGoal, setOptimizationGoal] = useState('max_return');
    const [maxPerAsset, setMaxPerAsset] = useState(40);
    const [hasOptimized, setHasOptimized] = useState(false);

    // Performance metrics
    const performanceMetrics = {
        returns: {
            current: 6.8,
            benchmark: 7.2,
            variance: -0.4,
            status: 'Underperforming',
            trend: 'declining'
        },
        fees: {
            current: 1.2,
            benchmark: 0.8,
            variance: 0.4,
            status: 'Above Average',
            trend: 'stable'
        },
        diversification: {
            score: 65,
            benchmark: 80,
            variance: -15,
            status: 'Below Average',
            trend: 'improving'
        },
        liquidity: {
            ratio: 15,
            benchmark: 10,
            variance: 5,
            status: 'Above Average',
            trend: 'stable'
        }
    };

    // Optimization opportunities
    const generateOptimizationOpportunities = () => {
        return [
            {
                category: 'Investment Strategy',
                priority: 'High',
                title: 'Rebalance Portfolio Allocation',
                description: 'Current allocation is overweight in domestic equities',
                impact: 'Medium',
                effort: 'Low',
                potential_benefit: '0.5-1.2% annual return improvement',
                cost: '$500-1,000',
                timeline: '2-4 weeks'
            },
            {
                category: 'Cost Management',
                priority: 'High',
                title: 'Negotiate Lower Management Fees',
                description: 'Current fees are above industry average',
                impact: 'High',
                effort: 'Medium',
                potential_benefit: '$2,400-3,600 annual savings',
                cost: '$200-500',
                timeline: '4-6 weeks'
            },
            {
                category: 'Tax Efficiency',
                priority: 'Medium',
                title: 'Implement Tax-Loss Harvesting',
                description: 'Opportunity to realize losses and offset gains',
                impact: 'Medium',
                effort: 'Medium',
                potential_benefit: '$1,500-2,500 tax savings',
                cost: '$300-600',
                timeline: '3-5 weeks'
            },
            {
                category: 'Diversification',
                priority: 'Medium',
                title: 'Increase International Exposure',
                description: 'Add international equities and bonds',
                impact: 'Medium',
                effort: 'Medium',
                potential_benefit: 'Improved risk-adjusted returns',
                cost: '$400-800',
                timeline: '4-6 weeks'
            },
            {
                category: 'Cash Management',
                priority: 'Low',
                title: 'Optimize Cash Holdings',
                description: 'Reduce excess cash and improve yield',
                impact: 'Low',
                effort: 'Low',
                potential_benefit: '$800-1,200 additional income',
                cost: '$100-300',
                timeline: '1-2 weeks'
            }
        ];
    };

    // Benchmark analysis
    const performBenchmarkAnalysis = () => {
        return {
            peer_comparison: {
                percentile: 35,
                description: 'Performing below 65% of similar SMSFs',
                key_issues: ['High fees', 'Poor diversification', 'Suboptimal asset allocation']
            },
            market_comparison: {
                vs_balanced_fund: -0.4,
                vs_equity_fund: -1.2,
                vs_bond_fund: 2.1,
                description: 'Underperforming growth assets, outperforming defensive assets'
            },
            risk_adjusted: {
                sharpe_ratio: 0.65,
                benchmark_sharpe: 0.78,
                description: 'Lower risk-adjusted returns than benchmark'
            }
        };
    };

    // Asset allocation analysis
    const assetAllocation = {
        current: {
            'Australian Equities': 45,
            'International Equities': 20,
            'Fixed Interest': 15,
            'Property': 10,
            'Cash': 10
        },
        recommended: {
            'Australian Equities': 35,
            'International Equities': 30,
            'Fixed Interest': 20,
            'Property': 10,
            'Cash': 5
        },
        rebalancing_needed: {
            'Australian Equities': -10,
            'International Equities': 10,
            'Fixed Interest': 5,
            'Property': 0,
            'Cash': -5
        }
    };

    useEffect(() => {
        setPerformanceData(performanceMetrics);
        setOptimizationOpportunities(generateOptimizationOpportunities());
        setBenchmarkAnalysis(performBenchmarkAnalysis());
    }, []);

    const renderOptimizer = () => {
        // Optimized allocations based on goal
        const optimizedAllocation = optimizationGoal === 'max_return'
            ? { ausShares: 40, intlShares: 40, property: 10, bonds: 5, cash: 5 }
            : optimizationGoal === 'min_risk'
            ? { ausShares: 20, intlShares: 15, property: 10, bonds: 35, cash: 20 }
            : { ausShares: 30, intlShares: 30, property: 15, bonds: 20, cash: 5 };
        
        const expectedReturn = optimizationGoal === 'max_return' ? 9.2 : optimizationGoal === 'min_risk' ? 5.8 : 7.5;
        const expectedRisk = optimizationGoal === 'max_return' ? 16.5 : optimizationGoal === 'min_risk' ? 8.2 : 12.0;
        
        return (
            <div className="space-y-6">
                {/* Optimizer Inputs */}
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold mb-4">Portfolio Optimizer</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Optimization Goal</label>
                            <select
                                value={optimizationGoal}
                                onChange={(e) => setOptimizationGoal(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="max_return">Maximize Return</option>
                                <option value="min_risk">Minimize Risk</option>
                                <option value="efficient_frontier">Efficient Frontier (Optimal Risk/Return)</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Max % Per Asset (Constraint)</label>
                            <input
                                {...getNumericInputProps()}
                                value={maxPerAsset}
                                onChange={(e) => setMaxPerAsset(Number(e.target.value) || 0)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="40"
                            />
                            <p className="text-xs text-gray-500 mt-1">Maximum allocation per asset class (%)</p>
                        </div>
                    </div>
                    
                    <div className="mt-6 flex justify-end">
                        <button
                            onClick={() => {
                                console.log('Optimize Portfolio clicked, goal:', optimizationGoal);
                                setHasOptimized(true);
                            }}
                            className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
                        >
                            Optimize Portfolio
                        </button>
                    </div>
                </div>
                
                {/* Current Portfolio */}
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold mb-4">Current Portfolio Holdings</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                        <div className="p-3 bg-gray-50 rounded flex justify-between">
                            <span>Australian Shares</span>
                            <span className="font-bold">25%</span>
                        </div>
                        <div className="p-3 bg-gray-50 rounded flex justify-between">
                            <span>International Shares</span>
                            <span className="font-bold">20%</span>
                        </div>
                        <div className="p-3 bg-gray-50 rounded flex justify-between">
                            <span>Property</span>
                            <span className="font-bold">15%</span>
                        </div>
                        <div className="p-3 bg-gray-50 rounded flex justify-between">
                            <span>Bonds</span>
                            <span className="font-bold">25%</span>
                        </div>
                        <div className="p-3 bg-gray-50 rounded flex justify-between">
                            <span>Cash</span>
                            <span className="font-bold">15%</span>
                        </div>
                    </div>
                </div>
                
                {/* Optimization Results */}
                {hasOptimized && (
                    <>
                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <h3 className="text-lg font-semibold mb-4">Optimized Portfolio</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                {/* Test 853: Optimized Allocation */}
                                <div className="col-span-full p-4 bg-green-50 border border-green-200 rounded-lg">
                                    <h4 className="font-semibold text-green-800 mb-3">Optimized Asset Allocation</h4>
                                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                                        {Object.entries(optimizedAllocation).map(([asset, pct]) => (
                                            <div key={asset} className="p-2 bg-white rounded flex justify-between">
                                                <span className="capitalize">{asset.replace(/([A-Z])/g, ' $1')}</span>
                                                <span className="font-bold text-green-700">{pct}%</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                
                                {/* Test 854: Expected Return */}
                                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                                    <div className="text-sm text-gray-600 mb-1">Expected Return</div>
                                    <div className="text-3xl font-bold text-blue-700">{expectedReturn}%</div>
                                    <div className="text-xs text-gray-500 mt-1">Projected annual return</div>
                                </div>
                                
                                {/* Test 855: Expected Risk */}
                                <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                                    <div className="text-sm text-gray-600 mb-1">Expected Risk</div>
                                    <div className="text-3xl font-bold text-orange-700">{expectedRisk}%</div>
                                    <div className="text-xs text-gray-500 mt-1">Volatility (std dev)</div>
                                </div>
                                
                                {/* Sharpe Ratio */}
                                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                                    <div className="text-sm text-gray-600 mb-1">Sharpe Ratio</div>
                                    <div className="text-3xl font-bold text-purple-700">
                                        {((expectedReturn - 2.5) / expectedRisk).toFixed(2)}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">Risk-adjusted return</div>
                                </div>
                            </div>
                        </div>
                        
                        {/* Test 856: Efficient Frontier Chart */}
                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <h3 className="text-lg font-semibold mb-4">Efficient Frontier</h3>
                            <ResponsiveContainer width="100%" height={400}>
                                <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                                    <CartesianGrid />
                                    <XAxis 
                                        {...getNumericInputProps()} 
                                        dataKey="risk" 
                                        name="Risk" 
                                        label={{ value: 'Risk (Volatility %)', position: 'insideBottom', offset: -10 }}
                                        domain={[0, 20]}
                                    />
                                    <YAxis 
                                        {...getNumericInputProps()} 
                                        dataKey="return" 
                                        name="Return" 
                                        label={{ value: 'Expected Return (%)', angle: -90, position: 'insideLeft' }}
                                        domain={[0, 12]}
                                    />
                                    <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                                    <Scatter 
                                        name="Efficient Frontier" 
                                        data={[
                                            { risk: 8.2, return: 5.8, name: 'Min Risk' },
                                            { risk: 10.0, return: 6.5, name: 'Conservative' },
                                            { risk: 12.0, return: 7.5, name: 'Balanced (Optimal)' },
                                            { risk: 14.5, return: 8.5, name: 'Growth' },
                                            { risk: 16.5, return: 9.2, name: 'Max Return' }
                                        ]} 
                                        fill="#3B82F6"
                                        line={{ stroke: '#3B82F6', strokeWidth: 2 }}
                                    />
                                    <Scatter 
                                        name="Current Portfolio" 
                                        data={[{ risk: 11.5, return: 6.8, name: 'Current' }]} 
                                        fill="#EF4444"
                                        shape="star"
                                    />
                                </ScatterChart>
                            </ResponsiveContainer>
                            <p className="text-sm text-gray-600 text-center mt-2">
                                Blue line shows optimal portfolios. Red star is your current position.
                            </p>
                        </div>
                        
                        {/* Test 857: Rebalancing Trades */}
                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <h3 className="text-lg font-semibold mb-4">Rebalancing Trades Required</h3>
                            <div className="space-y-2">
                                <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex justify-between items-center">
                                    <span className="text-sm"><strong>BUY:</strong> International Shares</span>
                                    <span className="font-bold text-green-700">+$50,000</span>
                                </div>
                                <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex justify-between items-center">
                                    <span className="text-sm"><strong>BUY:</strong> Australian Shares</span>
                                    <span className="font-bold text-green-700">+$30,000</span>
                                </div>
                                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex justify-between items-center">
                                    <span className="text-sm"><strong>SELL:</strong> Cash</span>
                                    <span className="font-bold text-red-700">-$40,000</span>
                                </div>
                                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex justify-between items-center">
                                    <span className="text-sm"><strong>SELL:</strong> Bonds</span>
                                    <span className="font-bold text-red-700">-$40,000</span>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        );
    };

    const renderOverview = () => (
        <div className="space-y-6">
            {/* Performance Summary */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Performance Summary</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {Object.entries(performanceData).map(([key, metric]) => (
                        <div key={key} className="p-4 border border-gray-200 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                                <h4 className="font-semibold text-gray-800 capitalize">
                                    {key.replace('_', ' ')}
                                </h4>
                                <span className={`px-2 py-1 text-xs rounded ${
                                    metric.status === 'Above Average' ? 'bg-green-100 text-green-800' :
                                    metric.status === 'Below Average' ? 'bg-red-100 text-red-800' :
                                    'bg-yellow-100 text-yellow-800'
                                }`}>
                                    {metric.status}
                                </span>
                            </div>
                            <div className="space-y-1">
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">Current:</span>
                                    <span className="font-bold text-gray-800">
                                        {key === 'returns' ? `${metric.current}%` :
                                         key === 'fees' ? `${metric.current}%` :
                                         key === 'diversification' ? `${metric.score}/100` :
                                         `${metric.ratio}%`}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">Benchmark:</span>
                                    <span className="font-medium text-gray-700">
                                        {key === 'returns' ? `${metric.benchmark}%` :
                                         key === 'fees' ? `${metric.benchmark}%` :
                                         key === 'diversification' ? `${metric.benchmark}/100` :
                                         `${metric.benchmark}%`}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">Variance:</span>
                                    <span className={`font-medium ${
                                        metric.variance > 0 ? 'text-red-600' : 'text-green-600'
                                    }`}>
                                        {metric.variance > 0 ? '+' : ''}{metric.variance}
                                        {key === 'returns' || key === 'fees' ? '%' :
                                         key === 'diversification' ? ' pts' : ' pp'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Asset Allocation Comparison */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Asset Allocation Analysis</h3>
                <div className="space-y-4">
                    {Object.keys(assetAllocation.current).map((asset, index) => (
                        <div key={index} className="space-y-2">
                            <div className="flex justify-between items-center">
                                <span className="font-medium text-gray-800">{asset}</span>
                                <span className="text-sm text-gray-600">
                                    Current: {assetAllocation.current[asset]}% | 
                                    Recommended: {assetAllocation.recommended[asset]}%
                                </span>
                            </div>
                            <div className="flex space-x-2">
                                <div className="flex-1 bg-gray-200 rounded-full h-2">
                                    <div 
                                        className="bg-blue-600 h-2 rounded-full"
                                        style={{ width: `${assetAllocation.current[asset]}%` }}
                                    ></div>
                                </div>
                                <div className="flex-1 bg-gray-200 rounded-full h-2">
                                    <div 
                                        className="bg-green-600 h-2 rounded-full"
                                        style={{ width: `${assetAllocation.recommended[asset]}%` }}
                                    ></div>
                                </div>
                            </div>
                            <div className="flex justify-between text-xs text-gray-500">
                                <span>Current</span>
                                <span className={`font-medium ${
                                    assetAllocation.rebalancing_needed[asset] > 0 ? 'text-green-600' :
                                    assetAllocation.rebalancing_needed[asset] < 0 ? 'text-red-600' : 'text-gray-600'
                                }`}>
                                    {assetAllocation.rebalancing_needed[asset] > 0 ? '+' : ''}
                                    {assetAllocation.rebalancing_needed[asset]}%
                                </span>
                                <span>Recommended</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Benchmark Comparison */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Benchmark Comparison</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center p-6 bg-blue-50 rounded-lg">
                        <div className="text-3xl font-bold text-blue-600">
                            {benchmarkAnalysis.peer_comparison?.percentile}th
                        </div>
                        <div className="text-sm text-blue-700">Peer Percentile</div>
                        <div className="text-xs text-gray-600 mt-2">
                            {benchmarkAnalysis.peer_comparison?.description}
                        </div>
                    </div>
                    <div className="text-center p-6 bg-green-50 rounded-lg">
                        <div className="text-3xl font-bold text-green-600">
                            {benchmarkAnalysis.risk_adjusted?.sharpe_ratio}
                        </div>
                        <div className="text-sm text-green-700">Sharpe Ratio</div>
                        <div className="text-xs text-gray-600 mt-2">
                            vs {benchmarkAnalysis.risk_adjusted?.benchmark_sharpe} benchmark
                        </div>
                    </div>
                    <div className="text-center p-6 bg-purple-50 rounded-lg">
                        <div className="text-3xl font-bold text-purple-600">
                            {benchmarkAnalysis.market_comparison?.vs_balanced_fund > 0 ? '+' : ''}
                            {benchmarkAnalysis.market_comparison?.vs_balanced_fund}%
                        </div>
                        <div className="text-sm text-purple-700">vs Balanced Fund</div>
                        <div className="text-xs text-gray-600 mt-2">
                            {benchmarkAnalysis.market_comparison?.description}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderOptimization = () => (
        <div className="space-y-6">
            {/* Optimization Opportunities */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Optimization Opportunities</h3>
                <div className="space-y-4">
                    {optimizationOpportunities.map((opportunity, index) => (
                        <div key={index} className={`border-l-4 p-4 rounded-r-lg ${
                            opportunity.priority === 'High' ? 'border-red-500 bg-red-50' :
                            opportunity.priority === 'Medium' ? 'border-yellow-500 bg-yellow-50' :
                            'border-blue-500 bg-blue-50'
                        }`}>
                            <div className="flex justify-between items-start mb-2">
                                <div className="flex-1">
                                    <h4 className="font-semibold text-gray-800">{opportunity.title}</h4>
                                    <p className="text-sm text-gray-600 mb-2">{opportunity.description}</p>
                                    <div className="flex space-x-4 text-sm text-gray-600">
                                        <span><strong>Category:</strong> {opportunity.category}</span>
                                        <span><strong>Impact:</strong> {opportunity.impact}</span>
                                        <span><strong>Effort:</strong> {opportunity.effort}</span>
                                        <span><strong>Timeline:</strong> {opportunity.timeline}</span>
                                    </div>
                                    <p className="text-sm text-green-700 font-medium mt-2">
                                        <strong>Potential Benefit:</strong> {opportunity.potential_benefit}
                                    </p>
                                </div>
                                <div className="flex flex-col items-end space-y-2 ml-4">
                                    <span className={`px-2 py-1 text-xs rounded ${
                                        opportunity.priority === 'High' ? 'bg-red-100 text-red-800' :
                                        opportunity.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                                        'bg-blue-100 text-blue-800'
                                    }`}>
                                        {opportunity.priority} Priority
                                    </span>
                                    <span className="text-xs text-gray-600">
                                        Cost: {opportunity.cost}
                                    </span>
                                </div>
                            </div>
                            <div className="flex space-x-2">
                                <button className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors">
                                    Implement
                                </button>
                                <button className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700 transition-colors">
                                    Learn More
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Implementation Roadmap */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Implementation Roadmap</h3>
                <div className="space-y-4">
                    <div className="flex items-center p-4 bg-red-50 rounded-lg">
                        <div className="w-3 h-3 bg-red-500 rounded-full mr-4"></div>
                        <div className="flex-1">
                            <h4 className="font-semibold text-gray-800">Phase 1: Immediate Actions (0-30 days)</h4>
                            <p className="text-sm text-gray-600">Portfolio rebalancing, cash optimization, fee negotiations</p>
                        </div>
                    </div>
                    <div className="flex items-center p-4 bg-yellow-50 rounded-lg">
                        <div className="w-3 h-3 bg-yellow-500 rounded-full mr-4"></div>
                        <div className="flex-1">
                            <h4 className="font-semibold text-gray-800">Phase 2: Strategic Changes (1-3 months)</h4>
                            <p className="text-sm text-gray-600">Tax-loss harvesting, international diversification</p>
                        </div>
                    </div>
                    <div className="flex items-center p-4 bg-blue-50 rounded-lg">
                        <div className="w-3 h-3 bg-blue-500 rounded-full mr-4"></div>
                        <div className="flex-1">
                            <h4 className="font-semibold text-gray-800">Phase 3: Ongoing Optimization (3-12 months)</h4>
                            <p className="text-sm text-gray-600">Continuous monitoring, strategy refinement, performance tracking</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderPerformance = () => (
        <div className="space-y-6">
            {/* Performance Trends */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Performance Trends</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Return Trends */}
                    <div>
                        <h4 className="font-semibold text-gray-700 mb-4">Return Performance</h4>
                        <div className="space-y-3">
                            {[
                                { period: '1 Year', current: 6.8, benchmark: 7.2, variance: -0.4 },
                                { period: '3 Years', current: 7.1, benchmark: 7.5, variance: -0.4 },
                                { period: '5 Years', current: 7.4, benchmark: 7.8, variance: -0.4 },
                                { period: 'Since Inception', current: 7.6, benchmark: 8.0, variance: -0.4 }
                            ].map((trend, index) => (
                                <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                                    <div>
                                        <div className="font-medium text-gray-800">{trend.period}</div>
                                        <div className="text-sm text-gray-600">
                                            Current: {trend.current}% | Benchmark: {trend.benchmark}%
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className={`font-bold ${
                                            trend.variance > 0 ? 'text-green-600' : 'text-red-600'
                                        }`}>
                                            {trend.variance > 0 ? '+' : ''}{trend.variance}%
                                        </div>
                                        <div className="text-xs text-gray-500">vs Benchmark</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Risk Metrics */}
                    <div>
                        <h4 className="font-semibold text-gray-700 mb-4">Risk Metrics</h4>
                        <div className="space-y-3">
                            {[
                                { metric: 'Volatility (Annual)', value: 12.4, benchmark: 11.8, status: 'Above Average' },
                                { metric: 'Maximum Drawdown', value: -18.2, benchmark: -15.6, status: 'Below Average' },
                                { metric: 'Sharpe Ratio', value: 0.65, benchmark: 0.78, status: 'Below Average' },
                                { metric: 'Beta (vs Market)', value: 1.05, benchmark: 1.00, status: 'Above Average' }
                            ].map((metric, index) => (
                                <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                                    <div>
                                        <div className="font-medium text-gray-800">{metric.metric}</div>
                                        <div className="text-sm text-gray-600">
                                            Current: {metric.value} | Benchmark: {metric.benchmark}
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <span className={`px-2 py-1 text-xs rounded ${
                                            metric.status === 'Above Average' ? 'bg-yellow-100 text-yellow-800' :
                                            metric.status === 'Below Average' ? 'bg-red-100 text-red-800' :
                                            'bg-green-100 text-green-800'
                                        }`}>
                                            {metric.status}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Cost Analysis */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Cost Analysis</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                        { type: 'Management Fees', current: 1.2, benchmark: 0.8, annual_cost: 14400, potential_savings: 4800 },
                        { type: 'Transaction Costs', current: 0.3, benchmark: 0.2, annual_cost: 3600, potential_savings: 1200 },
                        { type: 'Custody Fees', current: 0.1, benchmark: 0.1, annual_cost: 1200, potential_savings: 0 },
                        { type: 'Other Expenses', current: 0.2, benchmark: 0.1, annual_cost: 2400, potential_savings: 1200 }
                    ].map((cost, index) => (
                        <div key={index} className="p-4 border border-gray-200 rounded-lg">
                            <h4 className="font-semibold text-gray-800 mb-2">{cost.type}</h4>
                            <div className="space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">Current:</span>
                                    <span className="font-bold text-gray-800">{cost.current}%</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">Benchmark:</span>
                                    <span className="font-medium text-gray-700">{cost.benchmark}%</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">Annual Cost:</span>
                                    <span className="font-medium text-red-600">${cost.annual_cost.toLocaleString()}</span>
                                </div>
                                {cost.potential_savings > 0 && (
                                    <div className="flex justify-between">
                                        <span className="text-sm text-gray-600">Savings:</span>
                                        <span className="font-medium text-green-600">${cost.potential_savings.toLocaleString()}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-center">
                        <span className="font-medium text-gray-700">Total Annual Cost Savings Potential:</span>
                        <span className="text-xl font-bold text-green-600">$7,200</span>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="space-y-6 p-6 bg-gray-50 rounded-lg shadow-inner">
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-800">Performance Optimization</h2>
                <div className="flex space-x-2">
                    <button
                        onClick={() => setActiveTab('overview')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeTab === 'overview' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Overview
                    </button>
                    <button
                        onClick={() => setActiveTab('optimization')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeTab === 'optimization' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Optimization
                    </button>
                    <button
                        onClick={() => setActiveTab('performance')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeTab === 'performance' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Performance
                    </button>
                </div>
            </div>

            {/* Content based on active tab */}
            {activeTab === 'overview' && renderOverview()}
            {activeTab === 'optimization' && renderOptimizer()}
            {activeTab === 'performance' && renderPerformance()}

            {/* Action Buttons */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-600">
                        Analysis as of: {new Date().toLocaleDateString()}
                    </div>
                    <div className="flex space-x-4">
                        <button className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors">
                            Export Performance Report
                        </button>
                        <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
                            Schedule Review
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

SMSFPerformanceOptimization.propTypes = {
    smsfState: PropTypes.object.isRequired,
    setSmsfState: PropTypes.func.isRequired
};

export default SMSFPerformanceOptimization;
